import { OrlaAvatar, type AvatarState } from "../avatar";
import { useEffect, useRef, useState } from "react";
import { HERO_MESSAGES } from "./hero-messages";
import { MessageBubble } from "./MessageBubble";
import { OrlaWordmark } from "./OrlaWordmark";
import { RingBackdrop } from "./RingBackdrop";

/**
 * The opening is held for at least this long, even when she loads instantly. Her model is
 * cached after the first visit and can be ready in a few hundred milliseconds, which would
 * finish the opening before anyone had seen it start.
 */
const OPENING_MS = 1_200;

export function HeroStage() {
  const [arrived, setArrived] = useState(false);
  const openedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    openedAt.current = performance.now();
    return () => clearTimeout(timer.current);
  }, []);

  const onAvatarState = (state: AvatarState) => {
    if (state === "loading" || arrived || timer.current !== undefined) return;
    const remaining = Math.max(0, OPENING_MS - (performance.now() - openedAt.current));
    timer.current = setTimeout(() => setArrived(true), remaining);
  };

  return (
    <div className="hero-stage" data-intro={arrived ? "settled" : "gathered"}>
      <div className="hero-stage__figure">
        <RingBackdrop />
        <OrlaWordmark />
        <div className="hero-stage__avatar">
          <OrlaAvatar
            gesture={arrived ? { name: "Jump", token: 1 } : null}
            onStateChange={onAvatarState}
          />
        </div>
      </div>
      <ul className="hero-stage__conversation" aria-live="polite" aria-label="Orla says">
        {HERO_MESSAGES.map((message, order) => (
          <MessageBubble key={message.id} message={message} order={order} ready={arrived} />
        ))}
      </ul>
    </div>
  );
}
